import * as React from 'react';

import { EnterpriseBadge_UserState as EnterpriseBadgeUserState } from '__generated__/graphql-types';

import {
  VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE_ID,
  VERIFIED_SKILLS_MOCK_PATHWAY_NAME,
} from 'bundles/skills-common/shared/verified-skills/__mocks__/verifiedSkillsMockData';
import VerifiedSkillsBadgeDataContext, {
  type VerifiedSkillBadgeData,
} from 'bundles/skills-common/shared/verified-skills/context/pathway-badge/VerifiedSkillsBadgeDataContext';
import { useSkillPathwayEnrollmentDataContext } from 'bundles/skills-common/shared/verified-skills/context/skill-pathway/SkillPathwayEnrollmentDataContext';
import useGetBadgeTemplates from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/useGetBadgeTemplates';

type Props = {
  children: React.ReactNode;
};

const getMockVerifiedSkillsBadgeData = ({ loading }: { loading: boolean }): VerifiedSkillBadgeData => ({
  isBadgeIssued: false,
  badge: undefined,
  skillPathwayName: VERIFIED_SKILLS_MOCK_PATHWAY_NAME,
  loading,
});

const VerifiedSkillsBadgeDataProvider = ({ children }: Props) => {
  const { enrolledBadgeCollection, loading: loadingBadgeCollection } = useSkillPathwayEnrollmentDataContext();

  const badgeTemplateId = enrolledBadgeCollection?.badgeTemplateId || '';
  const isBadgeIssued = enrolledBadgeCollection?.userState === EnterpriseBadgeUserState.Issued;

  const { badgeTemplates, loading: loadingBadgeTemplates } = useGetBadgeTemplates(isBadgeIssued);

  // QA-only: short-circuit to deterministic mocked context values.
  const isMockBadgeTemplate = badgeTemplateId === VERIFIED_SKILLS_MOCK_BADGE_TEMPLATE_ID;
  if (isMockBadgeTemplate) {
    return (
      <VerifiedSkillsBadgeDataContext.Provider
        value={getMockVerifiedSkillsBadgeData({ loading: Boolean(loadingBadgeCollection || loadingBadgeTemplates) })}
      >
        {children}
      </VerifiedSkillsBadgeDataContext.Provider>
    );
  }

  const badge = badgeTemplates?.badges?.find(
    (template) => template?.badgeTemplate?.badgeTemplateId === badgeTemplateId
  );

  const contextValue = {
    isBadgeIssued,
    badge,
    skillPathwayName: enrolledBadgeCollection?.title || badge?.badgeTemplate?.title || '',
    loading: Boolean(loadingBadgeCollection || loadingBadgeTemplates),
  };

  return (
    <VerifiedSkillsBadgeDataContext.Provider value={contextValue}>{children}</VerifiedSkillsBadgeDataContext.Provider>
  );
};

export default VerifiedSkillsBadgeDataProvider;
